/**
 * Lightweight first-party pageview tracker.
 *
 * Calls `POST /api/track/pageview` (anonymous, no auth) on every route
 * change of the marketing surface. Designed to be fire-and-forget — any
 * failure (offline, server down, request blocked) silently no-ops so the
 * tracker can NEVER affect the UX.
 *
 * Identifies the visitor via a rotating client-only session UUID stored
 * in sessionStorage. This is *not* a permanent fingerprint — when the
 * tab closes the session ends, exactly like Plausible.
 */

const SESSION_KEY = 'ff_pv_session';

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      // crypto.randomUUID exists in all evergreen browsers we support.
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `pv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage can throw in private-mode Safari pre-2020 — degrade
    // gracefully to per-request anonymous tracking.
    return `pv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function detectDevice() {
  try {
    const ua = navigator.userAgent || '';
    if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
    return 'desktop';
  } catch { return 'desktop'; }
}

function readUtm(searchParams) {
  return {
    utm_source:   searchParams.get('utm_source')   || null,
    utm_medium:   searchParams.get('utm_medium')   || null,
    utm_campaign: searchParams.get('utm_campaign') || null,
    utm_term:     searchParams.get('utm_term')     || null,
    utm_content:  searchParams.get('utm_content')  || null,
  };
}

/**
 * Send a single pageview. Idempotent guard: if called with the same path
 * + sessionId within 1.5s (React StrictMode double-mount in dev) it's a
 * no-op so we don't double-count.
 */
let _lastSent = { path: null, at: 0 };
export function trackPageview(apiBase) {
  try {
    const path = window.location.pathname + window.location.search;
    const now = Date.now();
    if (_lastSent.path === path && now - _lastSent.at < 1500) return;
    _lastSent = { path, at: now };

    const params = new URLSearchParams(window.location.search);
    const body = {
      path: window.location.pathname,
      full_url: window.location.href,
      referrer: document.referrer || null,
      session_id: getOrCreateSessionId(),
      device: detectDevice(),
      ...readUtm(params),
    };
    // When the visitor is logged in, forward the Bearer token so the
    // backend can stamp `user_id` on the pageview (server-side JWT
    // decode — we never trust a client-supplied user_id). The admin
    // Traffic & Revenue dashboard uses this to filter out internal
    // browsing so QA doesn't skew acquisition metrics.
    const headers = { 'Content-Type': 'application/json' };
    try {
      const tok = localStorage.getItem('ff_token');
      if (tok) headers.Authorization = `Bearer ${tok}`;
    } catch { /* localStorage may be unavailable in private mode */ }
    // navigator.sendBeacon is preferable (survives nav unload) but it
    // sends Content-Type: text/plain which our FastAPI handler rejects.
    // Fetch with keepalive is the modern equivalent.
    fetch(`${apiBase}/api/track/pageview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    /* Never throw — analytics is non-critical */
  }
}
